import { Injectable } from '@angular/core';

import { merge, ReplaySubject, scan, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { Room, RoomType } from 'src/app/shared/models/room.model';

import { RoomsResult } from 'src/app/services/room/room.service';

@Injectable({ providedIn: 'root' })
export class RoomStoreService {
  private reset$ = new Subject<void>();
  private roomsInput$ = new ReplaySubject<RoomsResult>(1);

  private lastDistanceValue = 0;
  private lastFoundRoom: Room | null = null;

  readonly rooms$ = merge(
    this.reset$.pipe(map(() => ({ kind: 'reset' as const }))),
    this.roomsInput$.pipe(map((payload) => ({ kind: 'add' as const, payload })))
  ).pipe(
    scan(
      (acc, action) => {
        if (action.kind === 'reset') {
          this.lastDistanceValue = 0;
          this.lastFoundRoom = null;
          return { rooms: [], type: null };
        }

        const incomingType = action.payload.type;

        if (acc.type !== incomingType) {
          return this.resetWithRooms(incomingType, action.payload.rooms);
        }

        const existing = new Set(acc.rooms.map((r) => r.distanceValue));
        const appended = action.payload.rooms.map((room) =>
          existing.has(room.distanceValue) ? room : { ...room, distanceValue: this.lastDistanceValue++ }
        );

        this.lastFoundRoom = appended.at(-1) ?? this.lastFoundRoom;

        return {
          type: action.payload.type,
          rooms: [...acc.rooms, ...appended]
        };
      },
      { rooms: [], type: null }
    ),
    shareReplay(1)
  );

  private resetWithRooms(type: RoomType, rooms: Room[]): RoomsResult {
    this.lastDistanceValue = 0;

    const mapped = rooms.map((room) => ({
      ...room,
      distanceValue: this.lastDistanceValue++
    }));

    this.lastFoundRoom = mapped.at(-1) ?? null;

    return { type, rooms: mapped };
  }

  public addRooms(result: RoomsResult): void {
    this.roomsInput$.next(result);
  }

  public reset(): void {
    this.reset$.next();
  }

  public getLastFoundRoom(): Room {
    return this.lastFoundRoom;
  }
}
